<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { Multiselect } from 'vue-multiselect';
import type { FormModel } from '../../pages/cms/modules/articles/_runtime/services/ArticleCategoriesService';
import ArticleCategoriesService from '../../pages/cms/modules/articles/_runtime/services/ArticleCategoriesService';
import Permissions from '../../../shared/permissisons';

const emit = defineEmits<{
    (e: 'update:modelValue', value: string[]): void;
}>();

const props = defineProps<{
    modelValue: string[];
}>();

const { $log } = useLogging();
const { $alert } = useAlerts();
const { $permissions } = usePermissions();

const canCreateCategory = ref<boolean>(false);
const options = ref<Option[]>([]);
const categoryToAdd = ref<FormModel>({
    id: 0,
    name: ''
});

const categories = computed({
    get(): Option[]
    {
        return props.modelValue.map(p => ({
            text: p,
            value: p
        }));
    },
    set(value: Option[])
    {
        triggerModel(value);
    }
});

function triggerModel(value: Option[]): void
{
    emit('update:modelValue', value.map(p => p.value));
}

async function addCategory(category: string): Promise<void>
{
    category = category.substring(0, 100);
    categoryToAdd.value.name = category;

    if (!props.modelValue.includes(category))
    {
        const categoriesList = categories.value.slice();

        categoriesList.push({ text: category, value: category });
        categories.value = categoriesList;

        await ArticleCategoriesService.create(categoryToAdd.value);
    }
}

const onSearchChanged = useDebounceFn(async (category: string): Promise<void> =>
{
    try
    {
        if (category && category.length > 0 && category.length <= 100)
        {
            options.value = (await ArticleCategoriesService.getAutocomplete(category)).map(p => ({
                text: p,
                value: p
            }));
        }
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
}, 500);

onBeforeMount(async (): Promise<void> =>
{
    canCreateCategory.value = await $permissions.all([Permissions.Cms.ArticlesCategories.Edit]);
});
</script>

<template>
    <Multiselect
        v-model="categories"
        label="text"
        track-by="value"
        :options="options"
        :multiple="true"
        :taggable="canCreateCategory"
        :placeholder="$t('[[[Wyszukaj albo dodaj nową kategorię]]]')"
        :tag-placeholder="$t('[[[Dodaj nowa kategorię]]]')"
        :select-label="$t('[[[Naciśnij enter aby wybrać]]]')"
        :selected-label="$t('[[[Wybrano]]]')"
        :deselect-label="$t('[[[Naciśnij enter aby usunąć]]]')"
        :select-group-label="''"
        :deselect-group-label="''"
        @search-change="onSearchChanged"
        @tag="addCategory"
    >
        <template #noOptions>
            {{ $t('[[[Lista jest pusta]]]') }}
        </template>
        <template #noResult>
            {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
        </template>
    </Multiselect>
</template>
